import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Flame, Truck, Snowflake, Wind, Plug, Leaf } from "lucide-react";
import { useTran } from "@/hooks";
const CarbonActivityBreakdown = ({ targets, performance }) => {
  const { t } = useTran();
  // Safety check for required props
  if (!targets || !performance) {
    return null;
  }

  const scope1Activities = [
    {
      key: "stationaryCombustion",
      label: "Stationary Combustion",
      description: "Diesel, gas, LPG",
      unit: "tCO₂e",
      icon: <Flame className="h-4 w-4" />,
      color: "orange",
    },
    {
      key: "mobileCombustion",
      label: "Mobile Combustion",
      description: "Company vehicles, forklifts",
      unit: "tCO₂e",
      icon: <Truck className="h-4 w-4" />,
      color: "blue",
    },
    {
      key: "fugitiveEmissions",
      label: "Fugitive Emissions",
      description: "Refrigerants/AC leaks",
      unit: "tCO₂e",
      icon: <Snowflake className="h-4 w-4" />,
      color: "cyan",
    },
    {
      key: "processEmissions",
      label: "Process Emissions",
      description: "Industrial gases",
      unit: "tCO₂e",
      icon: <Wind className="h-4 w-4" />,
      color: "slate",
    },
  ];

  const scope2Activities = [
    {
      key: "purchasedElectricity",
      label: "Purchased Electricity",
      description: "Grid or renewable",
      unit: "tCO₂e",
      icon: <Plug className="h-4 w-4" />,
      color: "yellow",
    },
    {
      key: "purchasedSteam",
      label: "Purchased Steam",
      description: "Steam/heat",
      unit: "tCO₂e",
      icon: <Leaf className="h-4 w-4" />,
      color: "green",
    },
    {
      key: "purchasedCooling",
      label: "Purchased Cooling",
      description: "Chilled water",
      unit: "tCO₂e",
      icon: <Snowflake className="h-4 w-4" />,
      color: "blue",
    },
  ];

  const getStatusColor = (status) => {
    if (status === "on-track") return "bg-green-500 text-white font-semibold";
    if (status === "over-target")
      return "bg-orange-500 text-white font-semibold";
    return "bg-red-500 text-white font-semibold";
  };

  const renderActivityTable = (scope, activities, scopeLabel) => {
    const scopeTargetTotal = Object.values(targets[scope] || {}).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0,
    );
    const scopeActualTotal = Object.values(
      performance.activityBreakdown?.[scope] || {},
    ).reduce((sum, val) => sum + (Number(val) || 0), 0);
    return (
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>{`${scopeLabel} Activity Breakdown`}</span>
            <div className="text-sm font-normal text-slate-500 dark:text-slate-400">
              Total: {scopeActualTotal.toFixed(2)} /{" "}
              {scopeTargetTotal.toFixed(2)} {activities[0]?.unit}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">
                    {t("Activity")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    {t("Target")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    {t("Actual")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    {t("Variance")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    {t("Progress")}
                  </th>
                  {/* <th className="px-4 py-3 text-center font-medium">Status</th> */}
                </tr>
              </thead>
              <tbody>
                {activities.map((activity) => {
                  const target = targets[scope]?.[activity.key] || 0;
                  const actual =
                    performance.activityBreakdown?.[scope]?.[activity.key] || 0;
                  const variance = actual - target;
                  const progress = target > 0 ? (actual / target) * 100 : 0;
                  const status =
                    progress < 55
                      ? "at-risk"
                      : progress > 105
                        ? "over-target"
                        : "on-track";

                  return (
                    <tr
                      key={activity.key}
                      className="border-b border-slate-200 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/20"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {activity.icon}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {activity.label}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {activity.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-200">
                        {target.toFixed(2)} {activity.unit}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">
                        {actual.toFixed(2)} {activity.unit}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold text-base ${
                          variance >= 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {variance >= 0 ? "+" : ""}
                        {variance.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-700 dark:text-slate-200">
                        {progress.toFixed(1)}%
                      </td>
                      {/* <td className="px-4 py-3 text-center"> */}
                      {/*   <Badge className={getStatusColor(status)}>{status}</Badge> */}
                      {/* </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t("Activity-Level Breakdown")}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("Detailed analysis of emission sources and energy consumption")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {renderActivityTable("scope1", scope1Activities, "Scope 1")}
        {renderActivityTable("scope2", scope2Activities, "Scope 2")}
      </div>
    </div>
  );
};

export default CarbonActivityBreakdown;
